import {useRef} from 'react'
import {createPortal} from 'react-dom'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './DockedSidePanel'
import {Panel} from './Panel'
import {OverviewInfoContent} from '../overview/OverviewInfoContent'

export function OverviewInfoPanel() {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Info)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  if (!isDockedPanelOpen) return null

  // portal target lookup mirrors other panel components
  const panelContent = ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID)

  if (panelContent && !isCompact) {
    return createPortal(
      <Panel panelName={DockedPanel.Info}>
        <Panel.Body>
          <OverviewInfoContent />
        </Panel.Body>
      </Panel>,
      panelContent,
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={returnFocusRef}
      onClose={closePanel}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Info</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close info panel"
              icon={XIcon}
              variant="invisible"
              onClick={closePanel}
            />
          </div>
        </Dialog.Header>
      )}
    >
      <OverviewInfoContent />
    </Dialog>
  )
}

export function OverviewTimelinePanel() {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Timeline)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  if (!isDockedPanelOpen) return null

  const panelContent = ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID)

  if (panelContent && !isCompact) {
    return createPortal(
      <Panel panelName={DockedPanel.Timeline}>
        <Panel.Body>{null}</Panel.Body>
      </Panel>,
      panelContent,
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={returnFocusRef}
      onClose={closePanel}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Timeline</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close timeline panel"
              icon={XIcon}
              variant="invisible"
              onClick={closePanel}
            />
          </div>
        </Dialog.Header>
      )}
    />
  )
}
